import { createTheme } from '@mui/material/styles';

/**
 * Labourando MUI Theme — Dark-first design
 *
 * Mirrors the landing page (labourando.com.au) identity exactly:
 *   --bg:        hsl(160, 10%, 4%)           dark greenish-black
 *   --card:      hsl(160, 8%, 8%)            card surfaces
 *   --elevated:  hsl(160, 8%, 10%)           raised surfaces / inputs
 *   --border:    hsl(160, 8%, 16%)           subtle borders
 *   --glass:     hsla(160, 8%, 12%, 0.6)     frosted glass nav
 *   --fg:        hsl(60, 10%, 92%)           primary text
 *   --muted:     hsl(160, 8%, 55%)           secondary text
 *   --primary:   hsl(145, 72%, 40%)          brand green
 *   --secondary: hsl(38, 85%, 55%)           brand gold
 */

// Green scale — hue 145, saturation 72%
const brandGreen = {
  50: '#edfcf4',
  100: '#d3f8e2',
  200: '#9fefc0',
  300: '#61e598',
  400: '#24db70',
  500: '#1daf5a',   // --primary
  600: '#18914a',
  700: '#116936',
  800: '#0c4b26',
  900: '#072c16',
};

// Gold scale — hue 38, saturation 85%
const brandGold = {
  50: '#fdf5e7',
  100: '#fae7c6',
  200: '#f6d08e',
  300: '#f1b855',
  400: '#eea62b',   // --secondary
  500: '#d48d11',
  600: '#b3770f',
  700: '#84580b',
  800: '#5e3f08',
  900: '#392605',
};

// Landing page dark palette (exact CSS variable equivalents)
const dark = {
  bg:       '#0a0f0c',  // hsl(160, 10%, 4%)
  card:     '#131a16',  // hsl(160, 8%, 8%)
  elevated: '#171e1a',  // hsl(160, 8%, 10%)
  border:   '#262e2a',  // hsl(160, 8%, 16%)
  glass:    'hsla(160, 8%, 12%, 0.6)',
  fg:       '#ebebdf',  // hsl(60, 10%, 92%)
  muted:    '#838f8a',  // hsl(160, 8%, 55%)
};

const shared = {
  typography: {
    fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
    h1: { fontWeight: 700, fontSize: '2.5rem', letterSpacing: '-0.02em' },
    h2: { fontWeight: 600, fontSize: '2rem', letterSpacing: '-0.02em' },
    h3: { fontWeight: 600, fontSize: '1.75rem', letterSpacing: '-0.02em' },
    h4: { fontWeight: 600, fontSize: '1.5rem' },
    h5: { fontWeight: 600, fontSize: '1.25rem' },
    h6: { fontWeight: 600, fontSize: '1rem' },
    body1: { fontSize: '1rem', lineHeight: 1.6 },
    body2: { fontSize: '0.875rem', lineHeight: 1.5 },
    button: { fontWeight: 600, textTransform: 'none' },
  },
  spacing: 8,
  shape: { borderRadius: 12 },   // landing page --radius: 0.75rem ≈ 12px
};

/**
 * Build a MUI theme for the given mode ('light' | 'dark').
 */
export const buildTheme = (mode = 'dark') => {
  const isDark = mode === 'dark';

  return createTheme({
    palette: {
      mode,
      primary: {
        main: brandGreen[500],
        light: brandGreen[300],
        dark: brandGreen[600],
        contrastText: isDark ? dark.bg : brandGreen[900],
        ...brandGreen,
      },
      secondary: {
        main: brandGold[400],
        light: brandGold[300],
        dark: brandGold[500],
        contrastText: isDark ? dark.bg : brandGold[900],
        ...brandGold,
      },
      background: isDark
        ? { default: dark.bg, paper: dark.card, input: dark.elevated }
        : { default: '#ffffff', paper: '#f5f8f5', input: '#f8f9fa' },
      text: isDark
        ? { primary: dark.fg, secondary: dark.muted }
        : { primary: '#121A16', secondary: '#4A5568' },
      divider: isDark ? dark.border : 'rgba(0,0,0,0.12)',
      success: { main: brandGreen[500], light: brandGreen[200], dark: brandGreen[800] },
      info:    { main: '#3182ce', light: '#63b3ed', dark: '#2c5282' },
      warning: { main: '#d69e2e', light: '#ecc94b', dark: '#975a16' },
      error:   { main: '#e53e3e', light: '#fc8181', dark: '#c53030' },
      roadmap: {
        completed: brandGreen[500],
        pending: '#f6c34d',
        pendingBorder: '#f6c34d',
      },
      // Expose landing page tokens for sx props
      custom: {
        glass:      dark.glass,
        elevated:   dark.elevated,
        border:     dark.border,
        primaryDim: 'hsla(145, 72%, 40%, 0.1)',
        primaryDim2:'hsla(145, 72%, 40%, 0.15)',
        secondaryDim:'hsla(38, 85%, 55%, 0.1)',
        primaryGlow:'0 0 30px hsla(145, 72%, 40%, 0.3)',
        secondaryGlow:'0 0 30px hsla(38, 85%, 55%, 0.3)',
      },
    },
    ...shared,
    components: {
      // ─── Buttons: gradient + glow like landing page ───
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            textTransform: 'none',
            fontWeight: 600,
            padding: '0.5rem 1.25rem',
            transition: 'transform 0.15s, box-shadow 0.3s',
            '&:active': { transform: 'scale(0.97)' },
          },
          contained: isDark
            ? {
                background: 'linear-gradient(135deg, #1daf5a, #179c4b)',
                boxShadow: '0 0 30px hsla(145, 72%, 40%, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #1daf5a, #179c4b)',
                  boxShadow: '0 0 50px hsla(145, 72%, 40%, 0.4)',
                },
              }
            : {
                boxShadow: 'none',
                '&:hover': {
                  boxShadow: '0 1px 3px 0 rgba(0,0,0,0.1), 0 1px 2px 0 rgba(0,0,0,0.06)',
                },
              },
          outlined: isDark
            ? {
                borderColor: dark.border,
                color: dark.fg,
                '&:hover': {
                  borderColor: 'hsla(145, 72%, 40%, 0.3)',
                  backgroundColor: 'transparent',
                },
              }
            : {},
        },
      },

      // ─── Cards: elevated surface + glow on hover ───
      MuiCard: {
        styleOverrides: {
          root: isDark
            ? {
                borderRadius: 16,
                background: dark.elevated,
                border: `1px solid ${dark.border}`,
                boxShadow: 'none',
                transition: 'border-color 0.3s, box-shadow 0.3s',
                '&:hover': {
                  borderColor: 'hsla(145, 72%, 40%, 0.3)',
                  boxShadow: '0 0 40px hsla(145, 72%, 40%, 0.1)',
                },
              }
            : {
                borderRadius: 16,
                boxShadow: '0px 4px 12px rgba(0,0,0,0.05)',
              },
        },
      },

      // ─── Paper: match card surface ───
      MuiPaper: {
        styleOverrides: {
          root: isDark
            ? {
                borderRadius: 16,
                backgroundImage: 'none',
              }
            : { borderRadius: 16 },
          elevation1: {
            boxShadow: isDark
              ? 'none'
              : '0 1px 3px 0 rgba(0,0,0,0.1), 0 1px 2px 0 rgba(0,0,0,0.06)',
            ...(isDark && {
              border: `1px solid ${dark.border}`,
            }),
          },
        },
      },

      // ─── AppBar: frosted glass like landing header ───
      MuiAppBar: {
        styleOverrides: {
          root: isDark
            ? {
                backgroundColor: dark.glass,
                backdropFilter: 'blur(12px)',
                WebkitBackdropFilter: 'blur(12px)',
                borderBottom: `1px solid ${dark.border}`,
                boxShadow: 'none',
              }
            : {
                backgroundColor: brandGreen[800],
              },
        },
      },

      // ─── Inputs: elevated surface background ───
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': { borderRadius: 12 },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: isDark
            ? {
                backgroundColor: dark.elevated,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: dark.border,
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'hsla(145, 72%, 40%, 0.3)',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: brandGreen[500],
                },
              }
            : {
                backgroundColor: '#f8f9fa',
              },
        },
      },

      // ─── Dialogs: match card surface ───
      MuiDialog: {
        styleOverrides: {
          paper: isDark
            ? {
                background: dark.card,
                border: `1px solid ${dark.border}`,
              }
            : {},
        },
      },

      // ─── Drawer: match card surface ───
      MuiDrawer: {
        styleOverrides: {
          paper: isDark
            ? {
                background: dark.card,
                borderColor: dark.border,
              }
            : {},
        },
      },

      // ─── Chips: visible on dark surfaces ───
      MuiChip: {
        styleOverrides: {
          root: isDark
            ? {
                borderColor: 'hsla(160, 8%, 40%, 0.4)',
              }
            : {},
          filled: isDark
            ? {
                backgroundColor: 'hsla(160, 8%, 20%, 0.8)',
                color: dark.fg,
              }
            : {},
          outlined: isDark
            ? {
                backgroundColor: 'hsla(160, 8%, 16%, 0.4)',
              }
            : {},
          // Ensure colorDefault filled variant is distinguishable
          filledDefault: isDark
            ? {
                backgroundColor: 'hsla(160, 8%, 24%, 0.7)',
                color: dark.fg,
              }
            : {},
          outlinedDefault: isDark
            ? {
                borderColor: 'hsla(160, 8%, 40%, 0.5)',
                color: dark.muted,
              }
            : {},
        },
      },

      // ─── Tooltip ───
      MuiTooltip: {
        styleOverrides: {
          tooltip: isDark
            ? {
                backgroundColor: dark.elevated,
                border: `1px solid ${dark.border}`,
              }
            : {},
        },
      },

      MuiContainer: {
        styleOverrides: {
          root: { paddingLeft: '1rem', paddingRight: '1rem' },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          switchBase: {
            '&.Mui-checked': { color: brandGreen[500] },
            '&.Mui-checked + .MuiSwitch-track': { backgroundColor: brandGreen[500] },
          },
        },
      },
      MuiLink: {
        styleOverrides: {
          root: { color: isDark ? brandGreen[400] : brandGreen[600] },
        },
      },
      MuiStepIcon: {
        styleOverrides: {
          root: {
            '&.Mui-active': { color: brandGreen[500] },
            '&.Mui-completed': { color: brandGreen[500] },
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: isDark ? { borderColor: dark.border } : {},
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: isDark ? { borderColor: dark.border } : {},
        },
      },
    },
  });
};

// Default export — dark mode to match landing page
export default buildTheme('dark');